<script setup lang="ts">
/**
 * 稽核紀錄（`/audit`，`meta.roles: [superadmin]`）。
 *
 * 兩條原則同其他清單頁：
 * - **篩選送伺服器**（actor／action 都是後端 query，真的縮小結果集）。
 * - **分頁走 keyset**（後端回 `next` 游標；`(created_at, id)` 複合索引的形狀）——
 *   不是頁碼，所以只有「載入更多」，沒有跳頁。
 *
 * 動作下拉的選項來自 `/audit-logs/meta`（**後端字典全表**，不是 DISTINCT）：沒發生過的
 * 動作也要在下拉裡，否則使用者會以為那個動作不存在。
 * 進頁時可帶 `?actor=`（帳號管理頁的「操作紀錄」連結就是這樣過來的）。
 */
import { computed, onMounted, ref } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import FilterClearButton from '@/components/FilterClearButton.vue'
import UiButton from '@/components/UiButton.vue'
import { ApiError } from '@/api/client'
import * as ls from '@/api/ls'
import type { AdminUserRow, AuditCursor, AuditRow } from '@/api/ls'
import { usePersistedFilters } from '@/lib/persistedFilters'
import { fmtSeen } from '@/lib/staleness'

const route = useRoute()

const rows = ref<AuditRow[]>([])
const cursor = ref<AuditCursor | null>(null)
const actions = ref<Record<string, string>>({})
const admins = ref<AdminUserRow[]>([])
const loading = ref(true)
const loadingMore = ref(false)
const loadError = ref('')
const expanded = ref<Set<string>>(new Set())

// 批186：條件記住。網址帶 `?actor=`／`?action=` 時以網址為準（並記住、再把 query 拿掉）。
const pf = usePersistedFilters(
  'audit',
  { actorFilter: '', actionFilter: '' },
  { deepLink: { route, router: useRouter(), map: { actor: 'actorFilter', action: 'actionFilter' } } },
)
const { actorFilter, actionFilter } = pf.refs
const { dirty: filtersDirty } = pf
/** 下拉是 `@change` 驅動，程式改值不會觸發，所以清除後要自己重載。 */
const clearFilters = () => {
  pf.clear()
  void load()
}

const PAGE = 50

/** 動作下拉：依中文名排序（字典本身是按領域分組寫的，畫面上要好找）。 */
const actionOptions = computed(() =>
  Object.entries(actions.value)
    .map(([key, label]) => ({ key, label }))
    .sort((a, b) => a.label.localeCompare(b.label, 'zh-Hant')),
)

function errText(e: unknown, fallback: string): string {
  const d = e instanceof ApiError && e.payload ? (e.payload as { detail?: unknown }).detail : undefined
  if (d == null) return fallback
  return typeof d === 'string' ? d : JSON.stringify(d)
}

async function load() {
  loading.value = true
  expanded.value = new Set()
  try {
    const page = await ls.listAuditLogs({
      actor: actorFilter.value || undefined,
      action: actionFilter.value || undefined,
      limit: PAGE,
    })
    rows.value = page.rows
    cursor.value = page.next
    loadError.value = ''
  } catch (e) {
    loadError.value = errText(e, '讀取稽核紀錄失敗')
  } finally {
    loading.value = false
  }
}

async function loadMore() {
  if (!cursor.value) return
  loadingMore.value = true
  try {
    const page = await ls.listAuditLogs({
      actor: actorFilter.value || undefined,
      action: actionFilter.value || undefined,
      cursor: cursor.value,
      limit: PAGE,
    })
    rows.value = [...rows.value, ...page.rows]
    cursor.value = page.next
  } catch (e) {
    loadError.value = errText(e, '載入更多失敗')
  } finally {
    loadingMore.value = false
  }
}

onMounted(async () => {
  // 兩份下拉來源各自容錯：任一支失敗不該讓整頁空白（清單本身才是主體）。
  void ls
    .auditMeta()
    .then((m) => {
      actions.value = m.actions
    })
    .catch(() => {})
  void ls
    .listAdminUsers()
    .then((r) => {
      admins.value = r
    })
    .catch(() => {})
  await load()
})

function toggle(id: string) {
  const next = new Set(expanded.value)
  if (next.has(id)) next.delete(id)
  else next.add(id)
  expanded.value = next
}

function targetText(r: AuditRow): string {
  if (!r.target_type && !r.target_id) return '—'
  const id = r.target_id ?? ''
  // uuid 太長，畫面只顯示前 8 碼（完整值在 detail 展開裡）
  const short = /^[0-9a-f]{8}-[0-9a-f]{4}-/i.test(id) ? id.slice(0, 8) : id
  return `${r.target_type ?? '?'}｜${short || '—'}`
}

/**
 * 時間欄拆成兩行（批113 ⑨）。`fmtSeen` 回的是本地時區 `YYYY-MM-DD HH:mm`，一行排開約
 * 108px＋padding；拆成「日期／時分」兩行後這一欄只要 ~78px，省下的 40px 全歸摘要欄。
 * 解析失敗（`fmtSeen` 原樣回傳）或空值時退回單行，不去猜格式。
 */
function timeParts(iso: string | null | undefined): { date: string; time: string } {
  const s = fmtSeen(iso)
  const sp = s.indexOf(' ')
  return sp > 0 ? { date: s.slice(0, sp), time: s.slice(sp + 1) } : { date: s, time: '' }
}
</script>

<template>
  <div class="page">
    <div class="page-head">
      <h1>稽核紀錄</h1>
      <span class="count">{{ rows.length }}</span>
      <span class="spacer" />
      <label class="hf">
        <span>操作者</span>
        <select v-model="actorFilter" @change="load()">
          <option value="">全部</option>
          <option v-for="u in admins" :key="u.id" :value="u.email">{{ u.email }}</option>
        </select>
      </label>
      <label class="hf">
        <span>動作</span>
        <select v-model="actionFilter" @change="load()">
          <option value="">全部</option>
          <option v-for="a in actionOptions" :key="a.key" :value="a.key">{{ a.label }}</option>
        </select>
      </label>
      <FilterClearButton :dirty="filtersDirty" @click="clearFilters" />
    </div>

    <div v-if="loading" class="loading-wrap"><span class="spinner lg" /></div>

    <div v-else class="card">
      <p v-if="loadError" class="err">{{ loadError }}</p>
      <p class="subtle">
        我方後台每一次「改變客戶可見狀態或權限」的操作都會落在這裡（保留 365 天）。
        唯讀查詢、開站試算與告警確認刻意不收。
      </p>

      <div v-if="!rows.length" class="empty">
        <div class="empty-title">這個條件下沒有紀錄</div>
        <div class="empty-sub">換個操作者或動作看看；剛升級的環境要等下一次後台操作才會有第一筆。</div>
      </div>

      <template v-else>
        <div class="tbl-scroll" data-hscroll>
          <table class="tbl audit-tbl">
            <thead>
              <tr><th>時間</th><th>操作者</th><th>動作</th><th>對象</th><th>摘要</th><th></th></tr>
            </thead>
            <tbody>
              <template v-for="r in rows" :key="r.id">
                <!--
                  批113 ⑨（1366 跑版）：1366×768 下 `.card` 內只有 1068px，這張表卻要
                  1229～1283px。元凶是四個 `nowrap` 欄——「對象」被最長那一列
                  （`agent_config_template｜…`）撐到 397px，其餘列大片留白，摘要欄的
                  `min-width:320` 反而被擠出卡片外。修法是**只留動作欄 nowrap**
                  （最長「簽發供應商登入票」約 120px，折行反而更難掃），其餘三欄限寬折行、
                  時間拆兩行，摘要改吃剩餘寬度。
                -->
                <tr>
                  <td class="when">
                    <div>{{ timeParts(r.created_at).date }}</div>
                    <div v-if="timeParts(r.created_at).time" class="hm">
                      {{ timeParts(r.created_at).time }}
                    </div>
                  </td>
                  <td class="actor">{{ r.actor_email ?? '（系統）' }}</td>
                  <td class="nowrap">{{ r.action_label }}</td>
                  <td class="target"><code class="mono">{{ targetText(r) }}</code></td>
                  <td class="sum">{{ r.summary || '—' }}</td>
                  <td class="acts-cell">
                    <button v-if="r.detail" type="button" class="tab detail-link" @click="toggle(r.id)">
                      {{ expanded.has(r.id) ? '收合' : '明細' }}
                    </button>
                  </td>
                </tr>
                <tr v-if="r.detail && expanded.has(r.id)" class="detrow">
                  <td colspan="6">
                    <pre class="det">{{ JSON.stringify(r.detail, null, 2) }}</pre>
                    <div class="sub">action：<code class="mono">{{ r.action }}</code>｜target_id：<code class="mono">{{ r.target_id ?? '—' }}</code></div>
                  </td>
                </tr>
              </template>
            </tbody>
          </table>
        </div>

        <div v-if="cursor" class="more">
          <UiButton size="sm" :loading="loadingMore" @click="loadMore()">載入更多</UiButton>
        </div>
      </template>
    </div>
  </div>
</template>

<style scoped>
.page-head {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  margin-bottom: var(--sp-4);
}
.page-head h1 {
  margin: 0;
  font-size: var(--fs-20);
  color: var(--fg);
}
.count {
  font-size: var(--fs-12);
  color: var(--fg-3);
  background: var(--bg);
  padding: 1px 8px;
  border-radius: 999px;
}
.spacer {
  flex: 1;
}
.hf {
  display: flex;
  align-items: center;
  gap: var(--sp-2);
  font-size: var(--fs-12);
  color: var(--fg-3);
  white-space: nowrap;
}
.err {
  margin: 0 0 var(--sp-3);
  color: var(--danger);
  font-size: var(--fs-12);
}
/* 只剩「動作」欄用它（批113 ⑨）。 */
.nowrap {
  white-space: nowrap;
}
/* 時間：兩行（日期／時分），兩行都不折。 */
.when {
  white-space: nowrap;
  width: 1%;
}
/* ★ 不可叫 `.sub`——這個檔案下面已經有一個 `.sub`（明細列的 action／target_id 那行），
   同名會被後宣告的那份蓋掉，而且兩處語意不同、日後改其中一個會誤傷另一個。 */
.hm {
  margin-top: 2px;
  font-size: var(--fs-11);
  color: var(--fg-3);
}
/* 操作者：email 沒有空白可斷，不給 `overflow-wrap: anywhere` 就只能整段撐開（原本的 nowrap）。
   ★ 但 `anywhere` 會把這一欄的 min-content 降到**單一字元**，只寫 `max-width` 的話
     auto table layout 會把它壓到 89px、連 `visor.weng@vossic.com` 都折成兩行。
     下限 180px ＝我方信箱（`admin.demo@vossic.com` 實測 155px ＋ 24 padding）排得下一行；
     上限 200px 讓超長的代理商信箱折行而不是把整張表推出卡片。
   ★ 折成兩行**不會讓列變高**：時間欄本來就是兩行，這一欄跟著兩行是白賺的；
     所以下限只買「常見長度不折」，不為了罕見的長信箱去搶摘要的寬度。 */
.actor {
  min-width: 180px;
  max-width: 200px;
  overflow-wrap: anywhere;
}
/* 對象：原本整欄 nowrap，被最長那一列（`agent_config_template｜iplusone-cs 代管試用站共用
   AI 額度`）撐到 397px，其餘四十幾列大片留白——這就是這張表要橫捲的頭號元凶。
   ★ `code.mono` 預設是 inline，`max-width` 對 inline 元素無效（要 inline-block）；
     `｜` 兩側沒有空白，一樣得靠 `anywhere` 才斷得掉。
   ★ 同 `.actor`：`anywhere` 之後 min-content ＝單一字元，不給 td 下限的話這一欄會被壓到
     ~78px，連 `managed_host｜6b7df36d` 都折成三行（上限則讓超長的那列折行而不是撐開）。 */
.target {
  /* 210px ＝ 實測出來的「常見形狀一行排得下」：絕大多數列是 `<表名>｜<8 碼>`，最長的
     `license_request｜41deee85` 需要 175px（含 code 的 12px padding）＋ td 的 24px padding。
     190px 時它差 1px 就折成兩行——這一欄有 50 列，差這 1px 就是半張表變兩倍高。 */
  min-width: 210px;
  max-width: 240px;
}
.target code.mono {
  display: inline-block;
  max-width: 220px;
  overflow-wrap: anywhere;
}
/* 摘要吃剩下的寬度：原本的 `min-width:320px` 是「至少 320」，在總寬超標時等於把
   自己釘在 320 並把表推出卡片外。改成沒有下限、由 `table-layout: auto` 分配剩餘空間
   （摘要本來就會 wrap，是全表唯一可以無痛變高的欄）。 */
.sum {
  min-width: 0;
}
.detrow td {
  background: var(--bg-sunken);
}
.det {
  margin: 0;
  max-height: 320px;
  overflow: auto;
  font-family: var(--font-mono, monospace);
  font-size: var(--fs-11);
  color: var(--fg-2);
  white-space: pre-wrap;
  word-break: break-all;
}
.sub {
  display: block;
  margin-top: 4px;
  font-size: var(--fs-11);
  color: var(--fg-4);
}
.detail-link {
  padding: 4px 10px;
  font-size: var(--fs-11);
}
.more {
  display: flex;
  justify-content: center;
  padding-top: var(--sp-3);
}
/*
 * 手機／平板（批171）：六欄表在 <1200 放不下，摘要被擠成一欄三五個字、每列高達 200px。
 * 改成每筆三行的清單（不用通用的「欄名＋值」卡片——稽核一頁五十筆，要能快速往下掃）：
 *   第一行  時間・動作 ………… 〔明細〕
 *   第二行  操作者　對象
 *   第三行  摘要（整行寬）
 * 展開的明細列照舊整行寬。
 */
@media (--compact) {
  .audit-tbl,
  .audit-tbl > tbody {
    display: block;
  }
  .audit-tbl > thead {
    display: none;
  }
  .audit-tbl > tbody > tr:not(.detrow) {
    display: grid;
    grid-template-columns: max-content minmax(0, 1fr) auto;
    grid-template-areas:
      'when action acts'
      'actor target target'
      'sum sum sum';
    align-items: baseline;
    column-gap: var(--sp-3);
    row-gap: 2px;
    padding: 10px 0;
    border-bottom: 1px solid var(--border-2);
  }
  .audit-tbl > tbody > tr:hover {
    background: transparent;
  }
  .audit-tbl > tbody > tr > td {
    display: block;
    width: auto;
    min-width: 0;
    max-width: none;
    padding: 0;
    border: none;
  }
  .audit-tbl .when {
    grid-area: when;
    display: flex;
    gap: 6px;
    align-items: baseline;
  }
  .audit-tbl .hm {
    margin-top: 0;
  }
  .audit-tbl td.nowrap {
    grid-area: action;
    font-weight: 600;
    white-space: normal;
  }
  /* 選擇器要比 ui.css 的「操作欄自動釘右」更具體（那條在這裡不適用）。 */
  .tbl.audit-tbl > tbody > tr > td.acts-cell:last-child {
    grid-area: acts;
    position: static;
    box-shadow: none;
    background: transparent;
  }
  .audit-tbl .actor {
    grid-area: actor;
    font-size: var(--fs-11);
    color: var(--fg-3);
  }
  .audit-tbl .target {
    grid-area: target;
  }
  .audit-tbl .target code.mono {
    max-width: 100%;
  }
  .audit-tbl .sum {
    grid-area: sum;
    margin-top: 2px;
    line-height: 1.55;
  }
  .audit-tbl > tbody > tr.detrow,
  .audit-tbl > tbody > tr.detrow > td {
    display: block;
  }
  .audit-tbl > tbody > tr.detrow > td {
    padding: var(--sp-2);
  }
}
@media (--mobile) {
  .audit-tbl > tbody > tr:not(.detrow) {
    grid-template-areas:
      'when when acts'
      'action action action'
      'actor actor actor'
      'target target target'
      'sum sum sum';
  }
}
</style>
